import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import MangaCard from "@/components/feature/MangaCard";
import { useSeo } from "@/hooks/useSeo";
import { formatFavorites, mangaList, mangaMap } from "@/mocks/manga";

const BASE = "https://secretcomix.github.io";

export default function MangaDetail() {
  const { slug = "" } = useParams();
  const manga = mangaMap[slug];
  const related = manga
    ? mangaList.filter((m) => m.category === manga.category && m.slug !== slug).slice(0, 4)
    : [];

  useSeo({
    title: `${manga ? `${manga.title} - ${manga.categoryName}漫画介绍` : "作品未找到"} - 喵趣漫画网`,
    description: manga
      ? `${manga.title}是喵趣漫画网推荐的${manga.categoryName}类萌系漫画，${manga.summary}`
      : "你访问的漫画作品不存在，请返回首页重新浏览。",
    keywords: `喵趣漫画网,${manga ? manga.title : "萌系漫画"},${manga ? manga.categoryName : "治愈番"},萌系日常`,
    url: BASE + `/manga/${slug}`,
    type: "article",
    jsonLd: manga
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: manga.title,
            description: manga.summary,
            author: { "@type": "Organization", name: "喵趣漫画网" },
            publisher: { "@type": "Organization", name: "喵趣漫画网" },
            inLanguage: "zh-CN",
            mainEntityOfPage: BASE + `/manga/${slug}`,
            image: manga.cover,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: BASE + "/" },
              { "@type": "ListItem", position: 2, name: manga.categoryName, item: BASE + `/category/${manga.category}` },
              { "@type": "ListItem", position: 3, name: manga.title, item: BASE + `/manga/${slug}` },
            ],
          },
        ]
      : [],
  });

  if (!manga) {
    return (
      <Layout>
        <div className="container-page flex flex-col items-center py-24 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-3xl text-secondary-700">
            <i className="ri-error-warning-line" aria-hidden="true"></i>
          </span>
          <h1 className="mt-5 text-2xl font-bold text-foreground-950">这部作品好像迷路了</h1>
          <p className="mt-2 text-sm text-foreground-600">你访问的漫画不存在或已被调整，去看看其他萌系日常吧。</p>
          <Link to="/" className="btn-primary mt-6">返回首页</Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="container-page mt-6 md:mt-8">
        <nav className="mb-6 flex flex-wrap items-center gap-2 text-xs text-foreground-500" aria-label="面包屑">
          <Link to="/" className="hover:text-primary-700">首页</Link>
          <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          <Link to={`/category/${manga.category}`} className="hover:text-primary-700">{manga.categoryName}</Link>
          <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          <span className="text-foreground-800">{manga.title}</span>
        </nav>

        <div className="grid grid-cols-1 gap-8 overflow-hidden rounded-3xl border border-background-200 bg-background-50 p-5 md:p-8 lg:grid-cols-[300px_1fr]">
          <div className="relative aspect-[4/5] w-full overflow-hidden rounded-2xl bg-background-100 lg:aspect-auto lg:h-[420px]">
            <img
              src={manga.cover}
              alt={`${manga.title} 封面`}
              title={`${manga.title} - ${manga.categoryName}萌系漫画`}
              className="h-full w-full object-cover"
            />
          </div>

          <div className="flex flex-col">
            <div className="flex flex-wrap items-center gap-3">
              <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-bold text-secondary-900">{manga.categoryName}</span>
              <span className={`rounded-full px-3 py-1 text-xs font-bold text-background-50 ${manga.status === "连载中" ? "bg-secondary-500" : "bg-foreground-600"}`}>
                {manga.status}
              </span>
            </div>

            <h1 className="mt-4 text-2xl font-bold text-foreground-950 md:text-3xl">{manga.title}</h1>

            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-600">
              <span className="flex items-center gap-1">
                <i className="ri-star-fill text-accent-500" aria-hidden="true"></i>
                <strong className="text-foreground-950">{manga.rating.toFixed(1)}</strong> 分
              </span>
              <span>作者 · {manga.author}</span>
              <span className="flex items-center gap-1">
                <i className="ri-bookmark-line text-primary-600" aria-hidden="true"></i>
                {formatFavorites(manga.favorites)} 追更
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-play-list-line text-secondary-700" aria-hidden="true"></i>
                {manga.chapters} 话
              </span>
            </div>

            <div className="mt-4 flex flex-wrap gap-1.5">
              {manga.tags.map((t) => (
                <span key={t} className="rounded-full bg-background-100 px-3 py-1 text-xs font-medium text-foreground-700">{t}</span>
              ))}
            </div>

            <p className="mt-5 rounded-2xl bg-secondary-100/60 px-4 py-3 text-sm leading-7 text-foreground-700">
              {manga.summary}
            </p>

            <div className="mt-5 space-y-3 text-sm leading-7 text-foreground-700">
              {manga.intro.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>

            <div className="mt-6 flex items-center gap-3 rounded-2xl border border-background-200 bg-background-100/60 px-4 py-3 text-sm text-foreground-700">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary-500 text-background-50">
                <i className="ri-refresh-line text-lg" aria-hidden="true"></i>
              </span>
              <span>
                最近更新：<strong className="text-foreground-950">{manga.episode}</strong>
                <span className="ml-2 text-foreground-500">{manga.updateTime}</span>
              </span>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/faq" className="btn-primary px-6">
                <i className="ri-play-fill mr-1" aria-hidden="true"></i>
                开始阅读
              </Link>
              <Link to={`/category/${manga.category}`} className="btn-ghost">
                更多{manga.categoryName}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="container-page mt-12 md:mt-14">
        <div className="flex items-end justify-between">
          <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">同类作品推荐</h2>
          <Link to={`/category/${manga.category}`} className="hidden items-center gap-1 text-sm font-medium text-primary-700 hover:text-primary-800 sm:flex">
            更多 <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
          {related.map((m) => (
            <MangaCard key={m.slug} manga={m} />
          ))}
        </div>
      </section>
    </Layout>
  );
}